import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useNavigate } from 'react-router-dom';
import { useOrder } from '@/hooks/useOrders';
import { useTrackShipment } from '@/hooks/useShipping';
import { calculateOrderTotals, formatPrice, OrderStatus } from '@bookstore/shared';
import { 
  Package, 
  Truck, 
  CheckCircle2, 
  Clock, 
  MapPin, 
  Calendar,
  Loader2,
  RefreshCw,
  Download,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

const TrackOrderPage = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { data: order, isLoading: orderLoading } = useOrder(orderId);
  const trackMutation = useTrackShipment();
  const [trackingData, setTrackingData] = useState<any>(null);

    const totals = calculateOrderTotals(order?.subtotal || 0, order?.discount || 0, order?.shippingCharges || 0, 0);

  // 🚧 TEMPORARY: Auto-fetch tracking disabled (India Post API not available)
  // TODO: Uncomment when India Post tracking API is ready
  const handleRefreshTracking = async () => {
    if (!order?.shipping?.awb) {
    toast.error('No tracking number available');
    return;
  }

    // 🔴 COMMENTED OUT - India Post tracking API not available yet
    // TODO: Uncomment when India Post API credentials are ready
    // try {
    //   const result = await trackMutation.mutateAsync({ orderId: orderId!, shipmentId: order.shipping!.shipmentId!.toString() });
    //   if (result.success) {
    //     setTrackingData(result.data);
    //     toast.success('Tracking updated');
    //   }
    // } catch (error) {
    //   toast.error('Failed to fetch tracking');
    // }

    // ✅ TEMPORARY: Show message about manual tracking
    toast.info('Please check tracking ID with the courier partner. Live tracking will be available soon.');
  };

  if (orderLoading) {
    return (
      <div className="container-custom py-20 text-center">
        <Loader2 className="w-12 h-12 animate-spin text-primary-600 mx-auto mb-4" />
        <p className="text-gray-600">Loading order details...</p>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="container-custom py-20 text-center">
        <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Order Not Found</h2>
        <p className="text-gray-600 mb-6">The order you're looking for doesn't exist.</p>
        <button onClick={() => navigate('/orders')} className="btn-primary">
          View All Orders
        </button>
      </div>
    );
  }

  const statusSteps = [
    { 
      status: OrderStatus.PLACED, 
      label: 'Order Placed', 
      icon: CheckCircle2,
      description: 'Your order has been received'
    },
    { 
      status: OrderStatus.CONFIRMED, 
      label: 'Confirmed', 
      icon: CheckCircle2,
      description: 'Order confirmed and being processed'
    },
    { 
      status: OrderStatus.PACKED, 
      label: 'Packed', 
      icon: Package,
      description: 'Your order is packed and ready'
    },
    { 
      status: OrderStatus.SHIPPED, 
      label: 'Shipped', 
      icon: Truck,
      description: 'Package handed over to courier'
    },
    { 
      status: OrderStatus.OUT_FOR_DELIVERY, 
      label: 'Out for Delivery', 
      icon: Truck,
      description: 'Package is out for delivery'
    },
    { 
      status: OrderStatus.DELIVERED, 
      label: 'Delivered', 
      icon: CheckCircle2,
      description: 'Package successfully delivered'
    },
  ];

  const currentStepIndex = statusSteps.findIndex(step => step.status === order.orderStatus);
  const isCompleted = (index: number) => index <= currentStepIndex;
  const isCurrent = (index: number) => index === currentStepIndex;

  // Use tracking data if available, otherwise use order data
  const trackingHistory = trackingData?.history || order.shipping?.trackingHistory || [];
  const currentStatus = trackingData?.currentStatus || order.shipping?.trackingStatus || order.orderStatus;

  return (
    <>
      <Helmet>
        <title>Track Order #{order.orderNumber?.slice(0,8) || order.id.slice(0,8).toUpperCase()} | Bookstore</title>
      </Helmet>

      <div className="container-custom py-10">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/orders')}
            className="text-primary-600 hover:text-primary-700 mb-4 flex items-center gap-2"
          >
            ← Back to Orders
          </button>
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Track Order #{order.orderNumber?.slice(0,8) || order.id.slice(0,8).toUpperCase()}
              </h1>
              <p className="text-gray-600 mt-2">
                Ordered on {order.createdAt?.toDate?.()?.toLocaleDateString()}
              </p>
            </div>

          {order.shipping?.awb && (
            <div className="text-right">
              <p className="text-sm text-gray-600 mb-1">Tracking Number (AWB)</p>
              <p className="text-lg font-mono font-semibold text-gray-900">
                {order.shipping.awb}
                </p>
                <button
                  onClick={handleRefreshTracking}
                  disabled={trackMutation.isPending}
                  className="mt-2 text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
                >
                  <RefreshCw className={`w-4 h-4 ${trackMutation.isPending ? 'animate-spin' : ''}`} />
                  Refresh Tracking
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* LEFT: Tracking Timeline */}
          <div className="lg:col-span-2 space-y-8">
            {/* Status Progress */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
              <h2 className="text-xl font-semibold mb-8">Order Status</h2>

              <div className="relative">
                {statusSteps.map((step, index) => {
                  const Icon = step.icon;
                  const completed = isCompleted(index);
                  const current = isCurrent(index);

                  return (
                    <div key={step.status} className="relative pb-8 last:pb-0">
                      {/* Connector Line */}
                      {index < statusSteps.length - 1 && (
                        <div
                          className={`absolute left-6 top-12 w-0.5 h-full -ml-px ${
                            isCompleted(index + 1) ? 'bg-green-500' : 'bg-gray-300'
                          }`}
                        />
                      )}

                      {/* Step */}
                      <div className="relative flex items-start gap-4">
                        {/* Icon Circle */}
                        <div
                          className={`
                            flex items-center justify-center w-12 h-12 rounded-full border-2 transition-all
                            ${
                              completed
                                ? 'bg-green-500 border-green-500'
                                : current
                                ? 'bg-primary-500 border-primary-500 animate-pulse'
                                : 'bg-white border-gray-300'
                            }
                          `}
                        >
                          <Icon
                            className={`w-6 h-6 ${
                              completed || current ? 'text-white' : 'text-gray-400'
                            }`}
                          />
                        </div>

                        {/* Content */}
                        <div className="flex-1 pt-1">
                          <p
                            className={`font-semibold ${
                              completed || current ? 'text-gray-900' : 'text-gray-500'
                            }`}
                          >
                            {step.label}
                          </p>
                          <p className="text-sm text-gray-600 mt-1">{step.description}</p>
                          {current && order.updatedAt && (
                            <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {order.updatedAt.toDate?.()?.toLocaleString()}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* India Post Tracking Details */}
            {trackingHistory.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold">Tracking History</h2>
                  {order.shipping?.labelImageUrl && (
                    
                    <a  href={order.shipping.labelImageUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
                    >
                      <Download className="w-4 h-4" />
                      Download Label
                    </a>
                  )}
                </div>

                <div className="space-y-4">
                  {trackingHistory.map((event: any, index: number) => (
                    <div
                      key={index}
                      className="flex gap-4 pb-4 border-b border-gray-100 last:border-0"
                    >
                      <div className="flex-shrink-0">
                        <div className="w-2 h-2 rounded-full bg-primary-500 mt-2"></div>
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{event.status}</p>
                        {event.location && (
                          <p className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                            <MapPin className="w-3 h-3" />
                            {event.location}
                          </p>
                        )}
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(event.timestamp).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                {trackingData?.estimatedDelivery && (
                  <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-blue-800">
                      <Calendar className="w-5 h-5" />
                      <div>
                        <p className="text-sm font-medium">Estimated Delivery</p>
                        <p className="text-lg font-semibold">
                          {new Date(trackingData.estimatedDelivery).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* RIGHT: Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 sticky top-24">
              <h3 className="font-semibold text-gray-900 mb-4">Order Summary</h3>

              {/* Items */}
              <div className="space-y-3 mb-4 max-h-64 overflow-y-auto">
                {order.items.map((item) => (
                  <div key={item.productId} className="flex gap-3">
                    {item.imageUrl && (
                      <img
                        src={item.imageUrl}
                        alt={item.title}
                        className="w-16 h-20 object-cover rounded"
                      />
                    )}
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">{item.title}</p>
                      <p className="text-xs text-gray-600 mt-1">Qty: {item.quantity}</p>
                      <p className="text-sm font-semibold text-gray-900 mt-1">
                        {formatPrice((item.discountedPrice || item.price) * item.quantity)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Totals */}
              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="text-gray-900">{formatPrice(totals.subtotal)}</span>
                </div>
                {totals.discount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Discount</span>
                    <span className="text-green-600">-{formatPrice(totals.discount)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Shipping</span>
                  <span className="text-gray-900">
                    {order.shippingCharges === 0 ? (
                      <span className="text-green-600 font-medium">FREE</span>
                    ) : (
                      formatPrice(order.shippingCharges)
                    )}
                  </span>
                </div>
                {totals.tax > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Tax</span>
                    <span className="text-gray-900">{formatPrice(totals.tax)}</span>
                  </div>
                )}
                <div className="flex justify-between font-semibold text-lg pt-2 border-t">
                  <span>Total</span>
                  <span className="text-primary-700">{formatPrice(order.totalAmount)}</span>
                </div>
              </div>

              {/* Shipping Address */}
              <div className="mt-6 pt-6 border-t">
                <h4 className="font-semibold text-gray-900 mb-3">Delivery Address</h4>
                <div className="text-sm text-gray-600 space-y-1">
                  <p className="font-medium text-gray-900">{order.shippingAddress.name}</p>
                  <p>{order.shippingAddress.addressLine1}</p>
                  {order.shippingAddress.addressLine2 && <p>{order.shippingAddress.addressLine2}</p>}
                  <p>
                    {order.shippingAddress.city}, {order.shippingAddress.state} - {order.shippingAddress.pincode}
                  </p>
                  <p className="pt-2">Phone: {order.shippingAddress.phone}</p>
                  {order.shippingAddress.secondaryPhone && (
                    <p>Secondary Phone: {order.shippingAddress.secondaryPhone}</p>
                  )}
                </div>
              </div>

              {/* Shipping Provider Info */}
              {order.shipping?.provider === 'icarry' && (
                <div className="mt-6 pt-6 border-t">
                  <h4 className="font-semibold text-gray-900 mb-3">Shipping Info</h4>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm font-medium text-blue-900 flex items-center gap-2">
                      <Package className="w-4 h-4" />
                      iCarry Logistics
                    </p>
                    {order.shipping.awb && (
                      <p className="text-xs text-blue-700 mt-1 font-mono">
                        AWB: {order.shipping.awb}
                      </p>
                    )}
                    {order.shipping.courierName && (
                      <p className="text-xs text-blue-700 mt-1">
                        Courier: {order.shipping.courierName}
                      </p>
                    )}
                  </div>
                  {order.shipping.trackingUrl && (
                    <a
                      href={order.shipping.trackingUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1 mt-2"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Track on iCarry Website
                    </a>
                  )}
                </div>
              )}

              {order.shipping?.provider === 'manual' && (
                <div className="mt-6 pt-6 border-t">
                  <h4 className="font-semibold text-gray-900 mb-3">Shipping Info</h4>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                    <p className="text-sm font-medium text-gray-900 flex items-center gap-2">
                      <Truck className="w-4 h-4" />
                      {order.shipping.manualCourierName || 'Manual Courier'}
                    </p>
                    {order.shipping.manualTrackingNumber && (
                      <p className="text-xs text-gray-700 mt-1 font-mono">
                        Tracking: {order.shipping.manualTrackingNumber}
                      </p>
                    )}
                    {order.shipping.manualNotes && (
                      <p className="text-xs text-gray-600 mt-2 italic">
                        {order.shipping.manualNotes}
                      </p>
                    )}
                  </div>
                  {order.shipping.manualTrackingUrl && (
                    <a
                      href={order.shipping.manualTrackingUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1 mt-2"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Track Shipment Online
                    </a>
                  )}
                </div>
              )}

            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TrackOrderPage;